import React, { useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AuthPanel } from "@/components/auth/AuthPanel";
import { Logo } from "@/components/Logo";
import { setMeta, SITE_URL } from "@/lib/seo";

const Login = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || new URLSearchParams(location.search).get("redirect") || "/profile";

  useEffect(() => {
    setMeta({ title: "Sign in — StayFlow", description: "Sign in to StayFlow to book faster and track your stays.", url: `${SITE_URL}/login` });
  }, []);

  useEffect(() => {
    if (!loading && user) navigate(redirectTo, { replace: true });
  }, [user, loading, redirectTo, navigate]);

  const onSuccess = () => navigate(redirectTo, { replace: true });

  return (
    <div className="min-h-screen bg-paper flex flex-col md:flex-row" data-testid="login-page">
      {/* Brand / visual side */}
      <div className="relative hidden md:flex md:w-1/2 lg:w-[55%] bg-ink text-white overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=1400&q=70"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link to="/" className="inline-flex"><Logo variant="light" /></Link>
          <div>
            <h1 className="font-heading font-extrabold text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Your next stay,<br />just a tap away.
            </h1>
            <p className="mt-5 text-white/70 text-base max-w-md leading-relaxed">
              Sign in to save your wishlist across devices, breeze through checkout, and keep every booking in one place.
            </p>
          </div>
        </div>
      </div>

      {/* Auth side */}
      <div className="flex-1 flex flex-col">
        <div className="p-5 md:p-8">
          <button onClick={() => navigate(-1)} data-testid="login-back" className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-brand transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center px-5 pb-16">
          <div className="w-full max-w-sm">
            <div className="md:hidden mb-8"><Logo /></div>
            <h2 className="font-heading font-extrabold text-3xl tracking-tight text-ink">Welcome to StayFlow</h2>
            <p className="mt-2 text-ink-soft">Sign in or create your account in seconds.</p>

            <div className="mt-8">
              {loading ? (
                <div className="flex items-center justify-center h-40 text-ink-soft">
                  <Loader2 className="w-6 h-6 animate-spin text-brand" />
                </div>
              ) : (
                <AuthPanel onSuccess={onSuccess} recaptchaId="sf-recaptcha-login" />
              )}
            </div>

            <p className="mt-8 text-center text-xs text-ink-faint">
              By continuing you agree to our{" "}
              <Link to="/terms" className="text-brand hover:underline">Terms</Link> and{" "}
              <Link to="/privacy" className="text-brand hover:underline">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
